/**
 * MarketAreaZipsPage — Compliance · Market-Area ZIPs (FraudPoint in-market set).
 *
 * Bill 2026-06-13: "an admin-configurable set of market-area ZIP codes" drives
 * the FraudPoint in-market vs out-of-market decision. This editor IS that set.
 *
 * Procedure context (Online Applicants.pdf pp.2-3; FraudPointRiskGateService):
 * the LexisNexis FraudPoint MODERATE band (500-749) has a market-aware collect
 * rule — in-market needs a government photo ID; out-of-market needs photo ID +
 * SSN card. "In-market" per procedure = Phelps / Greene counties + bordering,
 * but the EXACT ZIP membership is a bank business decision Bill curates here —
 * NOT hardcoded (DO-NOT-GUESS rule #1: no ZIPs are seeded).
 *
 * Server precedence (FraudPointRiskGateService.IsOutOfMarket):
 *   • active set NON-EMPTY → out-of-market = (person ZIP not in the active set)
 *   • active set EMPTY (the shipped default) → fall back to the legacy state
 *     rule (out-of-state = out-of-market) — behavior UNCHANGED until ZIPs added.
 * Adding the FIRST active ZIP flips the bank onto ZIP-based market logic; the
 * editor surfaces that prominently.
 *
 * Contract: GET /v3/admin/market-area-zips (list incl. inactive),
 *   POST /v3/admin/market-area-zips/upsert, POST .../deactivate.
 *
 * 5 admin-quality bars:
 *   wired         — reads the admin list; writes upsert + deactivate; live data.
 *   configurable  — the entire in-market ZIP set is admin-driven.
 *   makes sense   — explains the precedence + the empty-set fallback inline;
 *                   shows whether the bank is on ZIP-mode or state-mode.
 *   modern control— 5-digit-validated inputs; inline add/edit; toggle active;
 *                   no free-text rank/SQL.
 *   no-breaking   — cannot hard-delete (deactivate only); ZIP validated to a
 *                   5-digit value; empty set is a safe, valid state (state
 *                   fallback). One row's edit can't corrupt others.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner, Empty } from '@/components/primitives';
import { TextField, NumberField } from '@/components/FormField';
import {
  PersonModalConfigApi,
  errMessage,
  isRouteNotLive,
  type MarketAreaZipRow,
} from '@/services/personModalConfigApi';

const ZIP_RE = /^\d{5}$/;

interface DraftState {
  zip: string;
  county: string;
  city: string;
  label: string;
  sortOrder: number | null;
}

const EMPTY_DRAFT: DraftState = { zip: '', county: '', city: '', label: '', sortOrder: null };

export function MarketAreaZipsPage(): JSX.Element {
  const client = useApiClient();
  const api = useMemo(() => new PersonModalConfigApi(client), [client]);

  const [rows, setRows] = useState<MarketAreaZipRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [routePending, setRoutePending] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [editingZip, setEditingZip] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // ── Load all ZIP rows (incl. inactive) ───────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      setRows(null);
      setLoadError(null);
      setRoutePending(false);
      try {
        const resp = await api.listMarketAreaZips(ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows([...resp.data]);
        } else {
          setRows([]);
          setLoadError(resp.error ?? 'Failed to load market-area ZIPs.');
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        if (isRouteNotLive(e)) {
          setRows([]);
          setRoutePending(true);
        } else {
          setRows([]);
          setLoadError(errMessage(e));
        }
      }
    })();
    return () => ctrl.abort();
  }, [api, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const activeRows = useMemo(() => (rows ?? []).filter((r) => r.isActive), [rows]);
  const inactiveRows = useMemo(() => (rows ?? []).filter((r) => !r.isActive), [rows]);
  const zipModeActive = activeRows.length > 0;

  const beginEdit = useCallback((r: MarketAreaZipRow) => {
    setEditingZip(r.zip);
    setDraft({
      zip: r.zip,
      county: r.county ?? '',
      city: r.city ?? '',
      label: r.label ?? '',
      sortOrder: r.sortOrder ?? null,
    });
    setFormError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingZip(null);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  }, []);

  // ── Upsert (add new or save edit) ────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const zip = draft.zip.trim();
    if (!ZIP_RE.test(zip)) {
      setFormError('ZIP must be exactly 5 digits.');
      return;
    }
    if (!editingZip && (rows ?? []).some((r) => r.zip.trim() === zip)) {
      setFormError(`ZIP ${zip} is already in the catalog — edit the existing row instead.`);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.upsertMarketAreaZip({
        zip,
        county: draft.county.trim() || null,
        city: draft.city.trim() || null,
        label: draft.label.trim() || null,
        isActive: true,
        sortOrder: draft.sortOrder,
      });
      setToast(editingZip ? `Saved ZIP ${zip}.` : `Added ZIP ${zip} to the in-market set.`);
      cancelEdit();
      refresh();
    } catch (e) {
      setFormError(
        isRouteNotLive(e)
          ? 'Write route not live in this branch/deploy yet. The editor is wired; saving works once /v3/admin/market-area-zips/* ships.'
          : errMessage(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, editingZip, rows, api, cancelEdit, refresh]);

  // ── Deactivate / reactivate ──────────────────────────────────────────────
  const handleDeactivate = useCallback(
    async (zip: string) => {
      setSaving(true);
      try {
        await api.deactivateMarketAreaZip(zip);
        setToast(`Deactivated ZIP ${zip} (kept for audit — not deleted).`);
        refresh();
      } catch (e) {
        setToast(errMessage(e));
      } finally {
        setSaving(false);
      }
    },
    [api, refresh],
  );

  const handleReactivate = useCallback(
    async (r: MarketAreaZipRow) => {
      setSaving(true);
      try {
        await api.upsertMarketAreaZip({
          zip: r.zip.trim(),
          county: r.county ?? null,
          city: r.city ?? null,
          label: r.label ?? null,
          isActive: true,
          sortOrder: r.sortOrder ?? null,
        });
        setToast(`Reactivated ZIP ${r.zip.trim()}.`);
        refresh();
      } catch (e) {
        setToast(errMessage(e));
      } finally {
        setSaving(false);
      }
    },
    [api, refresh],
  );

  // ── Render ────────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <Banner tone="err">
        Could not load market-area ZIPs: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading market-area ZIPs…" />;

  return (
    <>
      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        This is the <strong>admin-configurable in-market ZIP set</strong> behind the FraudPoint
        Moderate-band collect rule. When a person's FraudPoint score is <strong>Moderate (500–749)</strong>,
        an <strong>in-market</strong> applicant needs a government photo ID; an{' '}
        <strong>out-of-market</strong> applicant needs photo ID <em>plus</em> an SSN card. Per
        procedure, in-market is Phelps / Greene + bordering counties — but the exact ZIP membership
        is your decision here (no ZIPs are pre-seeded).
      </Banner>

      <Banner tone={zipModeActive ? 'info' : 'warn'}>
        {zipModeActive ? (
          <>
            <strong>ZIP-based market logic is ACTIVE.</strong> {activeRows.length} active ZIP
            {activeRows.length === 1 ? '' : 's'}. A person is in-market only when their 5-digit ZIP
            is in this set; everyone else is out-of-market.
          </>
        ) : (
          <>
            <strong>No active ZIPs — falling back to the state rule.</strong> Until you add at least
            one active ZIP, the gate treats in-state (MO) as in-market and out-of-state as
            out-of-market (unchanged legacy behavior). <strong>Adding the first active ZIP flips the
            bank onto ZIP-based market logic.</strong>
          </>
        )}
      </Banner>

      {routePending && (
        <Banner tone="warn">
          <strong>Route pending.</strong> <code>/v3/admin/market-area-zips</code> isn't live in this
          deploy yet. The editor is fully wired; it activates once the Function ships the routes +
          the migration runs.
        </Banner>
      )}

      {/* Add / edit form */}
      <Card title={editingZip ? `Edit ZIP ${editingZip}` : 'Add an in-market ZIP'}>
        <div className="maz-form">
          <TextField
            label="ZIP code"
            value={draft.zip}
            onChange={(v) => setDraft((d) => ({ ...d, zip: v.replace(/\D/g, '').slice(0, 5) }))}
            placeholder="65401"
            maxLength={5}
            monospace
            required
            disabled={!!editingZip}
            hint={editingZip ? 'ZIP is the key — create a new row to change it.' : '5 digits.'}
          />
          <TextField
            label="County"
            value={draft.county}
            onChange={(v) => setDraft((d) => ({ ...d, county: v }))}
            placeholder="Phelps"
            maxLength={60}
            hint="Optional — informational."
          />
          <TextField
            label="City"
            value={draft.city}
            onChange={(v) => setDraft((d) => ({ ...d, city: v }))}
            placeholder="Rolla"
            maxLength={60}
            hint="Optional — informational."
          />
          <TextField
            label="Label"
            value={draft.label}
            onChange={(v) => setDraft((d) => ({ ...d, label: v }))}
            placeholder="Phelps / Greene / bordering"
            maxLength={80}
            hint="Optional — your grouping tag for why this ZIP is in-market."
          />
          <NumberField
            label="Sort order"
            value={draft.sortOrder}
            onChange={(v) => setDraft((d) => ({ ...d, sortOrder: v }))}
            min={0}
            placeholder="(by ZIP)"
            hint="Optional display order."
          />
        </div>
        {formError && <Banner tone="err">{formError}</Banner>}
        <div className="maz-form__actions">
          {editingZip && (
            <Button variant="ghost" onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : editingZip ? 'Save ZIP' : 'Add ZIP'}
          </Button>
        </div>
      </Card>

      {/* Active set */}
      <Card
        title={
          <span>
            In-market ZIPs <Pill tone={zipModeActive ? 'ok' : 'muted'}>{activeRows.length} active</Pill>
            {inactiveRows.length > 0 && <Pill tone="muted">{inactiveRows.length} inactive</Pill>}
          </span>
        }
      >
        {activeRows.length === 0 ? (
          <Empty>No active in-market ZIPs. The gate uses the state fallback until you add one.</Empty>
        ) : (
          <table className="maz-table">
            <thead>
              <tr>
                <th>ZIP</th>
                <th>County</th>
                <th>City</th>
                <th>Label</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {activeRows.map((r) => (
                <tr key={r.zip}>
                  <td className="maz-table__zip">{r.zip.trim()}</td>
                  <td>{r.county ?? '—'}</td>
                  <td>{r.city ?? '—'}</td>
                  <td>{r.label ?? '—'}</td>
                  <td className="maz-table__actions">
                    <Button variant="ghost" onClick={() => beginEdit(r)} disabled={saving}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => void handleDeactivate(r.zip.trim())}
                      disabled={saving}
                    >
                      Deactivate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Inactive set (reactivatable; never deleted) */}
      {inactiveRows.length > 0 && (
        <Card title={<span>Inactive ZIPs <Pill tone="muted">{inactiveRows.length}</Pill></span>}>
          <p className="page-intro">
            Deactivated ZIPs are kept for audit and can be reactivated. They do not count toward the
            in-market set.
          </p>
          <table className="maz-table maz-table--muted">
            <tbody>
              {inactiveRows.map((r) => (
                <tr key={r.zip}>
                  <td className="maz-table__zip">{r.zip.trim()}</td>
                  <td>{r.county ?? '—'}</td>
                  <td>{r.city ?? '—'}</td>
                  <td>{r.label ?? '—'}</td>
                  <td className="maz-table__actions">
                    <Button
                      variant="ghost"
                      onClick={() => void handleReactivate(r)}
                      disabled={saving}
                    >
                      Reactivate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
